import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { setVizLoading } from '../../../../store/visualizerSlice';
import ProductGroupStep from '../ProductGroupStep/ProductGroupStep';
import StepNavigation from '../../StepNavigation/StepNavigation';
import PatternTab from '../BathFloor/PatternTab';
import styles from './Backsplash.module.css';

const TABS = ['Tile', 'Tile Lays', 'Grout'];
const DEFAULT_PATTERN_ID = 201858;
const DEFAULT_GROUT_ID = -1;
const FIXED_ARG3 = 147607;
// Rotation code — 254252 for horizontal lays, 254254 for vertical lays.
const ARG4_HORIZONTAL = 254252;
const ARG4_VERTICAL = 254254;
function isVerticalPattern(pattern) {
    const type = String(pattern?.type || '').toLowerCase();
    const name = String(pattern?.name || '').toLowerCase();
    return type.includes('vertical') || name.includes('vertical');
}

// Match any surface whose name contains "Backsplash" (case-insensitive).
// This covers scenes that split the backsplash into multiple surfaces —
// e.g. "Backsplash" + "Backsplash Hood Extension" for kitchens with a
// wood hood (C-1 / L-1 / Q-1) where tile extends up to the hood.
function isBacksplashSurface(name) {
    return /backsplash/i.test(String(name || ''));
}

function applyBackplash(patternId, tileId, groutId, surfaceName, dispatch, vertical = false) {
    const chamViz = document.getElementById('chamViz');
    if (!chamViz || !tileId) return;

    // When a pattern is chosen, use the composite sel; otherwise fall back to
    // the simple `tileId(groutId)` form so the visualizer still applies the
    // tile even before the user picks a lay.
    const arg4 = vertical ? ARG4_VERTICAL : ARG4_HORIZONTAL;
    const sel = patternId
        ? `${patternId}(${tileId},${groutId},${FIXED_ARG3},${arg4})`
        : `${tileId}(${groutId})`;

    dispatch(setVizLoading(true));

    chamViz.applyByFilter(
        patternId || tileId,
        '',
        function () {
            if (isBacksplashSurface(this.name)) return sel;
            return this.sel;
        },
        true
    );
}

function isMosaicProduct(product) {
    return String(product?.efficiency || '').toLowerCase().includes('mosaic');
}

export default function Backsplash({
    surfaceName = 'Backsplash',
    stepIndex,
    room = '',
    onViewAllApplied,
    onNext,
    onPrevious
}) {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('Tile');
    const [tileId, setTileId] = useState(null);
    const [patternId, setPatternId] = useState(DEFAULT_PATTERN_ID);
    const [isVertical, setIsVertical] = useState(false);
    const [groutId, setGroutId] = useState(DEFAULT_GROUT_ID);
    const [isMosaic, setIsMosaic] = useState(false);

    const handleTileSelect = useCallback((product) => {
        const mosaic = isMosaicProduct(product);
        setTileId(product.id);
        setIsMosaic(mosaic);
        // Mosaic tiles skip pattern lays — force the simple `tileId(groutId)` sel.
        applyBackplash(mosaic ? null : patternId, product.id, groutId, surfaceName, dispatch, isVertical);
        if (mosaic && activeTab === 'Tile Lays') setActiveTab('Tile');
    }, [patternId, groutId, surfaceName, dispatch, activeTab, isVertical]);

    const handlePatternSelect = useCallback((pattern) => {
        setPatternId(pattern.id);
        const vertical = isVerticalPattern(pattern);
        setIsVertical(vertical);
        if (!tileId || isMosaic) return;
        applyBackplash(pattern.id, tileId, groutId, surfaceName, dispatch, vertical);
    }, [tileId, groutId, surfaceName, dispatch, isMosaic]);

    const handleGroutSelect = useCallback((product) => {
        setGroutId(product.id);
        if (!tileId) return;
        applyBackplash(isMosaic ? null : patternId, tileId, product.id, surfaceName, dispatch, isVertical);
    }, [patternId, tileId, surfaceName, dispatch, isMosaic, isVertical]);

    return (
        <div className={styles.wrapper}>
            <StepNavigation
                title="Backsplash"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={onViewAllApplied}
            />

            <div className={styles.tabs}>
                {TABS.map(tab => {
                    const disabled = tab === 'Tile Lays' && isMosaic;
                    return (
                        <button
                            key={tab}
                            className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''} ${disabled ? styles.tabDisabled : ''}`}
                            onClick={() => { if (!disabled) setActiveTab(tab); }}
                            disabled={disabled}
                            title={disabled ? 'Mosaic tiles do not support pattern lays' : undefined}
                        >
                            {tab}
                        </button>
                    );
                })}
            </div>

            <div className={styles.tabContent}>
                {activeTab === 'Tile' && (
                    <div className={styles.productArea}>
                        <ProductGroupStep
                            title=""
                            stepName="Backsplash"
                            seriesId="9781"
                            surfaceName={surfaceName}
                            stepIndex={stepIndex}
                            room={room}
                            onViewAllApplied={onViewAllApplied}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            hideNav
                            onProductSelect={handleTileSelect}
                        />
                    </div>
                )}

                {activeTab === 'Tile Lays' && (
                    <PatternTab
                        selectedPatternId={patternId}
                        onPatternSelect={handlePatternSelect}
                    />
                )}

                {activeTab === 'Grout' && (
                    <div className={styles.productArea}>
                        <ProductGroupStep
                            title=""
                            stepName="BacksplashGrout"
                            seriesId="8311"
                            surfaceName={surfaceName}
                            stepIndex={stepIndex}
                            room={room}
                            onViewAllApplied={onViewAllApplied}
                            onNext={onNext}
                            onPrevious={onPrevious}
                            hideNav
                            noFilter
                            onProductSelect={handleGroutSelect}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

Backsplash.propTypes = {
    surfaceName: PropTypes.string,
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
